import { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';

import styles from './TripRouteMap.module.scss';
import { TripStopCoordinate } from '../../../../../store/services/tripsApi';
import { useGoogleMaps } from '../../../../../hooks/useGoogleMaps';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import LocationIcon from '../../../../../assets/icons/LocationIcon.png';

interface TripRouteMapProps {
  routeCoordinates: TripStopCoordinate[];
  onMarkerClick?: (coordinate: TripStopCoordinate, index: number) => void;
  mobileHeight?: number;
}

const PIN_COLORS = {
  first: '#22c55e',
  last: '#f97316',
  middle: '#0098F0',
  only: '#0098F0',
} as const;

function buildPinIconDataUri(fill: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40"><path fill="${fill}" d="M16 0C7.2 0 0 7.2 0 16c0 10 16 24 16 24s16-14 16-24C32 7.2 24.8 0 16 0z"/><circle fill="#fff" cx="16" cy="14" r="5"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const DEFAULT_CENTER = {
  lat: 20.0,
  lng: 0.0,
};

const DEFAULT_ZOOM = 2;
const DESKTOP_SINGLE_MARKER_ZOOM = 7;
const MOBILE_SINGLE_MARKER_ZOOM = 8;
const DESKTOP_MIN_FIT_ZOOM = 3;
const MOBILE_MIN_FIT_ZOOM = 4;

const TripRouteMap: FC<TripRouteMapProps> = ({ routeCoordinates = [], onMarkerClick, mobileHeight }) => {
  const { isLoaded: isGoogleMapsLoaded, loadError } = useGoogleMaps();
  const isMobile = useIsMobile();
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const getMobileMapHeight = useMemo(() => (mobileHeight || 250), [mobileHeight]);

  const markerIconsByRole = useMemo(() => {
    if (!isGoogleMapsLoaded || !window.google?.maps) {
      return null;
    }
    const w = 28;
    const h = 34;
    const make = (fill: string) => ({
      url: buildPinIconDataUri(fill),
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(w / 2, h),
    });
    return {
      first: make(PIN_COLORS.first),
      last: make(PIN_COLORS.last),
      middle: make(PIN_COLORS.middle),
      only: make(PIN_COLORS.only),
    };
  }, [isGoogleMapsLoaded]);

  const fallbackMarkerIcon = useMemo(() => {
    if (!isGoogleMapsLoaded || !window.google?.maps) {
      return undefined;
    }
    return {
      url: LocationIcon,
      scaledSize: new window.google.maps.Size(28, 28),
      anchor: new window.google.maps.Point(14, 28),
    };
  }, [isGoogleMapsLoaded]);

  const simplePath = useMemo(() => {
    return routeCoordinates.map(coord => ({
      lat: coord.latitude,
      lng: coord.longitude,
    }));
  }, [routeCoordinates]);

  const polylineOptions = useMemo(
    () => ({
      path: simplePath,
      strokeColor: '#0098F0',
      strokeOpacity: 0.85,
      strokeWeight: 3,
      geodesic: true,
    }),
    [simplePath]
  );

  const markers = useMemo(() => {
    const markerData: Array<{
      id: string;
      position: { lat: number; lng: number };
      name: string;
      coordinate: TripStopCoordinate;
      index: number;
      pinRole: NonNullable<TripStopCoordinate['pinRole']>;
    }> = [];

    const n = routeCoordinates.length;
    routeCoordinates.forEach((coordinate, index) => {
      const pinRole: NonNullable<TripStopCoordinate['pinRole']> =
        coordinate.pinRole ?? (n === 1 ? 'only' : index === 0 ? 'first' : index === n - 1 ? 'last' : 'middle');
      markerData.push({
        id: `${coordinate.name}-${index}-${coordinate.latitude}-${coordinate.longitude}`,
        position: {
          lat: Number(coordinate.latitude),
          lng: Number(coordinate.longitude),
        },
        name: coordinate.name,
        coordinate: coordinate,
        index: index,
        pinRole,
      });
    });

    return markerData;
  }, [routeCoordinates]);

  const getDenseMarkersCenter = useCallback((): { lat: number; lng: number } | null => {
    if (markers.length < 3) {
      return null;
    }

    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    markers.forEach(marker => {
      minLat = Math.min(minLat, marker.position.lat);
      maxLat = Math.max(maxLat, marker.position.lat);
      minLng = Math.min(minLng, marker.position.lng);
      maxLng = Math.max(maxLng, marker.position.lng);
    });

    const latSpan = Math.max(maxLat - minLat, 0.01);
    const lngSpan = Math.max(maxLng - minLng, 0.01);
    const radiusLat = Math.max(latSpan * 0.2, 0.01);
    const radiusLng = Math.max(lngSpan * 0.2, 0.01);

    let bestCount = 0;
    let bestDistanceScore = Number.POSITIVE_INFINITY;
    let bestCenter: { lat: number; lng: number } | null = null;

    markers.forEach(anchor => {
      const neighbors: Array<{ lat: number; lng: number }> = [];
      let distanceScore = 0;

      markers.forEach(candidate => {
        const latDiff = Math.abs(candidate.position.lat - anchor.position.lat);
        const lngDiff = Math.abs(candidate.position.lng - anchor.position.lng);
        const isNeighbor = latDiff <= radiusLat && lngDiff <= radiusLng;

        if (!isNeighbor) {
          return;
        }

        neighbors.push(candidate.position);
        distanceScore += latDiff + lngDiff;
      });

      if (neighbors.length === 0) {
        return;
      }

      const hasBetterCluster =
        neighbors.length > bestCount || (neighbors.length === bestCount && distanceScore < bestDistanceScore);

      if (!hasBetterCluster) {
        return;
      }

      const center = neighbors.reduce(
        (acc, point) => {
          acc.lat += point.lat;
          acc.lng += point.lng;
          return acc;
        },
        { lat: 0, lng: 0 }
      );

      bestCenter = {
        lat: center.lat / neighbors.length,
        lng: center.lng / neighbors.length,
      };
      bestCount = neighbors.length;
      bestDistanceScore = distanceScore;
    });

    return bestCenter;
  }, [markers]);

  const fitMapToMarkers = useCallback(() => {
    if (!mapInstance || !window.google?.maps) {
      return;
    }

    if (markers.length === 0) {
      mapInstance.setCenter(DEFAULT_CENTER);
      mapInstance.setZoom(DEFAULT_ZOOM);
      return;
    }

    if (markers.length === 1) {
      mapInstance.setCenter(markers[0].position);
      mapInstance.setZoom(isMobile ? MOBILE_SINGLE_MARKER_ZOOM : DESKTOP_SINGLE_MARKER_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    markers.forEach(marker => {
      bounds.extend(marker.position);
    });

    const denseMarkersCenter = getDenseMarkersCenter();

    mapInstance.fitBounds(
      bounds,
      isMobile
        ? {
            top: 28,
            right: 18,
            bottom: 28,
            left: 18,
          }
        : {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20,
          }
    );

    window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
      if (denseMarkersCenter) {
        mapInstance.panTo(denseMarkersCenter);
      }

      const currentZoom = mapInstance.getZoom();
      const minZoom = isMobile ? MOBILE_MIN_FIT_ZOOM : DESKTOP_MIN_FIT_ZOOM;

      if (typeof currentZoom === 'number' && currentZoom < minZoom) {
        mapInstance.setZoom(minZoom);
      }
    });
  }, [getDenseMarkersCenter, isMobile, mapInstance, markers]);

  useEffect(() => {
    fitMapToMarkers();
  }, [fitMapToMarkers]);

  useEffect(() => {
    if (!mapInstance || !window.google?.maps) {
      return;
    }

    const handleResize = () => {
      window.google.maps.event.trigger(mapInstance, 'resize');
      fitMapToMarkers();
    };

    const rafId = window.requestAnimationFrame(handleResize);
    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [fitMapToMarkers, getMobileMapHeight, isMobile, mapInstance]);

  if (loadError) {
    return (
      <Box className={styles.mapContainer}>
        <Box className={styles.mapPlaceholder}>
          <Typography
            variant="h7Bold"
            className={styles.mapTitle}
            sx={{ color: 'red' }}
          >
            Error loading Google Maps
          </Typography>
          <Typography
            variant="h11"
            className={styles.mapSubtitle}
          >
            {loadError.message}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!isGoogleMapsLoaded) {
    return (
      <Box className={styles.mapContainer}>
        <Box className={styles.mapPlaceholder}>
          <Typography
            variant="h7Bold"
            className={styles.mapTitle}
          >
            Loading Map...
          </Typography>
          <Typography
            variant="h11"
            className={styles.mapSubtitle}
          >
            Please wait while we load your route
          </Typography>
        </Box>
      </Box>
    );
  }

  const mapContainerStyle = {
    width: '100%',
    height: isMobile ? `${getMobileMapHeight}px` : '100%',
  };

  const mapOptions = {
    disableDefaultUI: true,
    gestureHandling: 'greedy' as const,
    zoomControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
  };

  return (
    <Box className={styles.mapContainer}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={DEFAULT_ZOOM}
        center={DEFAULT_CENTER}
        options={mapOptions}
        onLoad={map => setMapInstance(map)}
      >
        {routeCoordinates.length > 1 && <Polyline options={polylineOptions} />}
        {markers.map(marker => {
          const icon = markerIconsByRole?.[marker.pinRole] ?? fallbackMarkerIcon ?? undefined;
          return (
            <Marker
              key={marker.id}
              position={marker.position}
              title={marker.name}
              icon={icon}
              onClick={() => onMarkerClick?.(marker.coordinate, marker.index)}
            />
          );
        })}
      </GoogleMap>
    </Box>
  );
};

export default TripRouteMap;
